import React, { useState, useEffect } from 'react';
import { sunatService } from 'services/sunatService';
import { SunatConfig, SunatEnv, SunatComprobante } from 'types/sunat.types';

// ============================================================
// ConfigSunatPage — Configuración de SUNAT / comprobantes
// Acceso: ADMIN
// ============================================================

const MASKED = '***';

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const Toggle: React.FC<{ value: boolean; onChange: (v: boolean) => void; disabled?: boolean }> = ({ value, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!value)}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
      ${value ? 'bg-blue-600' : 'bg-slate-300'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
      ${value ? 'translate-x-6' : 'translate-x-1'}`}/>
  </button>
);

const Field: React.FC<{
  label:       string;
  hint?:       string;
  required?:   boolean;
  children:    React.ReactNode;
}> = ({ label, hint, required, children }) => (
  <div>
    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {hint && <p className="text-slate-400 text-xs mt-1">{hint}</p>}
  </div>
);

const inputCls = 'w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed';
const selectCls = inputCls;

const DEFAULT: SunatConfig = {
  emiteComprobante:    false,
  env:                 'BETA',
  ruc:                 '',
  tipoComprobante:     'BOLETA',
  serieBoleta:         'B001',
  serieFactura:        'F001',
  solUsuario:          '',
  solClave:            '',
  certificadoPath:     '',
  certificadoClave:    '',
  permitirEdicionDias: 0,
};

const ConfigSunatPage: React.FC = () => {
  const [form,     setForm]     = useState<SunatConfig>(DEFAULT);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [success,  setSuccess]  = useState(false);

  // Flags: ¿el usuario modificó las claves enmascaradas?
  const [solClaveModificada,  setSolClaveModificada]  = useState(false);
  const [certClaveModificada, setCertClaveModificada] = useState(false);

  useEffect(() => {
    sunatService.get()
      .then(data => { setForm(data); })
      .catch(e => setError(e instanceof Error ? e.message : 'Error al cargar configuración SUNAT'))
      .finally(() => setLoading(false));
  }, []);

  const set = <K extends keyof SunatConfig>(field: K, value: SunatConfig[K]) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleSolClaveChange = (v: string) => {
    set('solClave', v);
    setSolClaveModificada(true);
  };

  const handleCertClaveChange = (v: string) => {
    set('certificadoClave', v);
    setCertClaveModificada(true);
  };

  const handleGuardar = async () => {
    setSaving(true); setError(null); setSuccess(false);
    try {
      const payload: SunatConfig = {
        ...form,
        solClave:         solClaveModificada  ? form.solClave         : MASKED,
        certificadoClave: certClaveModificada ? form.certificadoClave : MASKED,
      };
      const updated = await sunatService.update(payload);
      setForm(updated);
      setSolClaveModificada(false);
      setCertClaveModificada(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3500);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const disabled = !form.emiteComprobante;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Cabecera */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold">Configuración SUNAT</h1>
        <p className="text-blue-200 mt-1">Emisión electrónica de comprobantes de pago</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">{error}</div>
      )}

      {/* Activar / Entorno */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-slate-800">Emitir comprobantes de pago</p>
            <p className="text-xs text-slate-400 mt-0.5">Activa la integración con SUNAT en cada cobro</p>
          </div>
          <Toggle value={form.emiteComprobante} onChange={v => set('emiteComprobante', v)} />
        </div>

        {form.emiteComprobante && (
          <div className="pt-2 border-t border-slate-100">
            <Field label="Entorno" required>
              <select
                value={form.env}
                onChange={e => set('env', e.target.value as SunatEnv)}
                className={selectCls}
              >
                <option value="BETA">BETA (pruebas)</option>
                <option value="PRODUCCION">PRODUCCIÓN</option>
              </select>
            </Field>
            {form.env === 'PRODUCCION' && (
              <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
                </svg>
                <p className="text-amber-700 text-xs font-medium">
                  Modo producción — los comprobantes emitidos son legalmente válidos ante SUNAT.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Datos del emisor */}
      <div className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5 ${disabled ? 'opacity-60' : ''}`}>
        <h2 className="text-sm font-bold text-slate-700">Datos del emisor</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="RUC" required hint="11 dígitos">
            <input
              type="text" maxLength={11}
              value={form.ruc}
              onChange={e => set('ruc', e.target.value)}
              disabled={disabled}
              placeholder="20XXXXXXXXX"
              className={inputCls}
            />
          </Field>
          <Field label="Tipo de comprobante" required>
            <select
              value={form.tipoComprobante}
              onChange={e => set('tipoComprobante', e.target.value as SunatComprobante)}
              disabled={disabled}
              className={selectCls}
            >
              <option value="BOLETA">Boleta de venta</option>
              <option value="FACTURA">Factura</option>
            </select>
          </Field>
          <Field label="Serie Boleta" hint="Ej: B001">
            <input
              type="text" maxLength={4}
              value={form.serieBoleta}
              onChange={e => set('serieBoleta', e.target.value)}
              disabled={disabled}
              placeholder="B001"
              className={inputCls}
            />
          </Field>
          <Field label="Serie Factura" hint="Ej: F001">
            <input
              type="text" maxLength={4}
              value={form.serieFactura}
              onChange={e => set('serieFactura', e.target.value)}
              disabled={disabled}
              placeholder="F001"
              className={inputCls}
            />
          </Field>
          <Field label="Días para editar cobro" hint="0 = sin restricción">
            <input
              type="number" min={0}
              value={form.permitirEdicionDias}
              onChange={e => set('permitirEdicionDias', Number(e.target.value))}
              disabled={disabled}
              className={inputCls}
            />
          </Field>
        </div>
      </div>

      {/* Credenciales SOL */}
      <div className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5 ${disabled ? 'opacity-60' : ''}`}>
        <h2 className="text-sm font-bold text-slate-700">Credenciales SOL (SUNAT Operaciones en Línea)</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Usuario SOL" required>
            <input
              type="text"
              value={form.solUsuario}
              onChange={e => set('solUsuario', e.target.value)}
              disabled={disabled}
              placeholder="USUARIO123"
              className={inputCls}
            />
          </Field>
          <Field label="Clave SOL" required>
            <input
              type="password"
              value={solClaveModificada ? form.solClave : ''}
              onChange={e => handleSolClaveChange(e.target.value)}
              disabled={disabled}
              placeholder={form.solClave === MASKED ? 'Sin cambios (ya configurada)' : 'Ingresar clave SOL'}
              className={inputCls}
              autoComplete="new-password"
            />
            {form.solClave === MASKED && !solClaveModificada && (
              <p className="text-xs text-slate-400 mt-1">
                Clave guardada — déjalo vacío para no modificarla
              </p>
            )}
          </Field>
        </div>
      </div>

      {/* Certificado digital */}
      <div className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5 ${disabled ? 'opacity-60' : ''}`}>
        <h2 className="text-sm font-bold text-slate-700">Certificado digital (.pfx)</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Ruta del certificado" hint="Ruta absoluta al archivo .pfx en el servidor">
            <input
              type="text"
              value={form.certificadoPath}
              onChange={e => set('certificadoPath', e.target.value)}
              disabled={disabled}
              placeholder="/opt/certs/empresa.pfx"
              className={inputCls}
            />
          </Field>
          <Field label="Clave del certificado">
            <input
              type="password"
              value={certClaveModificada ? form.certificadoClave : ''}
              onChange={e => handleCertClaveChange(e.target.value)}
              disabled={disabled}
              placeholder={form.certificadoClave === MASKED ? 'Sin cambios (ya configurada)' : 'Ingresar clave del certificado'}
              className={inputCls}
              autoComplete="new-password"
            />
            {form.certificadoClave === MASKED && !certClaveModificada && (
              <p className="text-xs text-slate-400 mt-1">
                Clave guardada — déjalo vacío para no modificarla
              </p>
            )}
          </Field>
        </div>
      </div>

      {/* Footer con botón guardar */}
      <div className="flex items-center justify-between bg-white rounded-2xl border border-slate-100 shadow-sm px-6 py-4">
        <div>
          {success && (
            <span className="flex items-center gap-1.5 text-green-600 text-sm font-semibold">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd"/>
              </svg>
              Configuración guardada correctamente
            </span>
          )}
        </div>
        <button
          onClick={handleGuardar}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-700 hover:bg-blue-800
                     text-white text-sm font-bold transition-colors shadow-sm disabled:opacity-60"
        >
          {saving && <SpinnerIcon />}
          Guardar configuración
        </button>
      </div>
    </div>
  );
};

export default ConfigSunatPage;
